import React, { useCallback } from "react";
import { differenceInMilliseconds } from "date-fns";
import { useIdleTimer } from "react-idle-timer";

import { AlertStatus } from "merchant-portal-common/Alert";
import { tracking } from "merchant-portal-frontend/src/analytics";
import { useAlert } from "merchant-portal-frontend/src/providers/AlertProvider";
import { useAuth } from "merchant-portal-frontend/src/providers/AuthProvider";

/*
	JWT expires after 1 hour
	If the user was active 10 minutes before the token expired, refresh the token
	If the user had no activity 10 minutes before the token expired, log the user out
 */
export const ONE_HOUR = 1000 * 60 * 60;
export const TEN_MINUTES = 1000 * 60 * 10;
const THROTTLE_EVENTS = 1000;

const RefreshTimer: React.FC = () => {
	const { isAuthenticated, logout, refreshBreadToken, breadToken } = useAuth();
	const alert = useAlert();

	const breadTokenTimeout = useCallback(() => {
		if (!breadToken?.claims.exp) {
			return 0;
		}
		const { exp } = breadToken.claims;
		return exp * 1000;
	}, [breadToken]);

	useIdleTimer({
		timeout: ONE_HOUR,
		eventsThrottle: THROTTLE_EVENTS,
		onIdle: () => {
			if (isAuthenticated) {
				alert({
					message: "You have been logged out due to inactivity",
					status: AlertStatus.Info,
				});
				tracking.trackLogoutInactivity();
				logout();
			}
		},
		onAction: () => {
			if (
				isAuthenticated &&
				differenceInMilliseconds(new Date(breadTokenTimeout()), Date.now()) < TEN_MINUTES
			) {
				// Try to refresh the token. If we fail, that means the Okta JWT is expired, and we should log the user out.
				refreshBreadToken().catch(() => {
					tracking.trackLogoutInactivity();
					logout();
				});
			}
		},
	});

	return null;
};

export { RefreshTimer };
